// Enums matching backend
export const Region = {
  NORD_WEST: 'nord-west',
  SUED: 'sued',
  OST: 'ost',
} as const;
export type Region = (typeof Region)[keyof typeof Region];

export const Role = {
  ADMIN: 'ROLE_ADMIN',
  USER: 'ROLE_USER',
  PLANT_EMPLOYEE: 'ROLE_PLANT_EMPLOYEE',
  TRUSTEE: 'ROLE_TRUSTEE',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

// Display labels (German)
export const RegionLabels: Record<Region, string> = {
  [Region.NORD_WEST]: 'Nord-West',
  [Region.SUED]: 'Süd',
  [Region.OST]: 'Ost',
};

export const RoleLabels: Record<Role, string> = {
  [Role.ADMIN]: 'Administrator',
  [Role.USER]: 'Benutzer',
  [Role.PLANT_EMPLOYEE]: 'Werksmitarbeiter',
  [Role.TRUSTEE]: 'Treuhänder',
};

// Meldungskategorien (entsprechen ReportType im Backend)
export const ReportCategory = {
  MARKET: 'market',
  ENVIRONMENTAL: 'environmental',
  ACCIDENT: 'accident',
} as const;
export type ReportCategory = (typeof ReportCategory)[keyof typeof ReportCategory];

export const ReportCategoryLabels: Record<ReportCategory, string> = {
  [ReportCategory.MARKET]: 'Markt',
  [ReportCategory.ENVIRONMENTAL]: 'Umwelt',
  [ReportCategory.ACCIDENT]: 'Unfall',
};

// Entity types
export interface Company {
  id: number;
  name: string;
  plants?: Plant[];
  csiMember: boolean;
  createdAt: string;
  updatedAt?: string;
}

// Phase 42 (D-16): Rueckmeldung ueber entfernte/neu angelegte Meldungen nach dem Speichern einer
// Werksstilllegung. Wird ausschliesslich vom Backend berechnet (PlantActivationProcessor) und in
// der PATCH-Antwort mitgeliefert -- das Frontend rechnet nichts nach (siehe plantActivationNotice.ts).
export interface PlantActivationEntry {
  form: string;
  period: string;
}

export interface PlantActivationEffects {
  change: 'closed' | 'reopened' | 'rescheduled';
  removed: PlantActivationEntry[];
  created: PlantActivationEntry[];
}

export interface Plant {
  id: number;
  name: string;
  company?: Company;
  region: Region;
  users?: User[];
  kilns?: PlantKiln[];
  // D-04/D-05: Stilllegungsdatum, dieselbe einschliessende Datumsregel wie PlantKiln.activeUntil
  // (siehe isPlantActive in kilnActivity.ts).
  activeUntil?: string | null;
  // D-16: nur in der PATCH-Antwort gefuellt, serverseitig berechnet -- niemals selbst setzen/schreiben.
  activationEffects?: PlantActivationEffects | null;
  createdAt: string;
  updatedAt?: string;
}

// Phase 28 (D-05): Drehofen als Werks-Stammdatum. Oefen werden stillgelegt (activeUntil), nicht
// geloescht (D-06) -- Meldezeilen (RotaryKiln) zeigen per nullable FK auf einen PlantKiln und
// ueberleben dessen Loeschen.
//
// `plant` ist eine IRI (string), KEIN eingebettetes Plant-Objekt (Korrektur, Plan 28-07 Task 1):
// gegen `/api/plant_kilns` verifiziert (curl, 2026-09-01) -- die Relation traegt serverseitig nur
// die Gruppen 'plantkiln:read'/'plantkiln:write' (PlantKiln.php:60), keine Plant-Eigenschaft
// fuehrt 'plantkiln:read', also normalisiert API Platform sie ausschliesslich als IRI. Umgekehrt
// fehlt `plant` in den ueber `Plant.kilns` eingebetteten PlantKiln-Objekten komplett (nur
// 'plant:read'-Felder), deshalb bleibt das Feld optional.
export interface PlantKiln {
  id: number;
  name: string;
  plant?: string;
  sortOrder: number;
  activeFrom?: string | null;
  activeUntil?: string | null;
  allowsOwnMeasurements: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: Role[];
  isActive: boolean;
  company?: Company;
  plants?: Plant[];
  enabledReportCategories: string[];
  createdAt: string;
  updatedAt?: string;
}

// API response types for hydra collections
export interface HydraCollection<T> {
  'hydra:member': T[];
  'hydra:totalItems': number;
}

// Phase 35 (D-01 ff.): Emissionsbericht-Import. Der JSON-Vertrag fuer `sourceData`/`resultData`
// ist am Docblock ueber `EmissionReportImport::$sourceData` (backend/src/Entity/EmissionReportImport.php)
// festgeschrieben -- beide Spalten sind IDENTISCH strukturiert (D-05-Voraussetzung), diese Typen
// spiegeln genau diesen Vertrag. Aenderungen dort ziehen Aenderungen hier nach sich.
export type EmissionReportImportStatus = 'open' | 'unreadable' | 'accepted';

export interface EmissionReportCellValue {
  value: string | null;
  belowLimit: boolean;
  empty: 'probe' | 'nn' | 'nb' | 'blimit' | 'unread' | 'hand' | null;
}

export interface EmissionReportMeasurement {
  index: number;
  date: string | null;
  dateRaw: string | null;
  operatingMode: string | null;
  time: string | null;
  valueBasis: 'ten_percent' | 'actual' | null;
  oxygenReference: string | null;
  values: Record<string, EmissionReportCellValue>;
  /**
   * Punkt 4 (WR-15/WR-12, 35.1-04): NUR in `resultData`, von der Vorschau beim Loeschen einer
   * Messung gesetzt. Nur `true` schliesst eine Messung von der Uebernahme aus; Laenge und
   * Reihenfolge des Arrays bleiben dabei unveraendert (kein Neudurchnummerieren mehr).
   */
  excluded?: boolean;
}

export interface EmissionReportLogEntry {
  severity: string;
  code: string;
  field: string | null;
  measurement: number | null;
  table: string | null;
  message: string;
}

/**
 * Ein Massenstrom-Einzelwert (Phase 40, Plan 40-04, D-17). Struktur analog zu
 * `EmissionReportCellValue`, aber mit eigenem Datum/Betriebszustand je Eintrag statt an der
 * gemeinsamen Messung zu haengen -- Massenstroeme bilden keine Messung (D-08). `value` steht
 * bereits in der Feldeinheit der Komponente (siehe `EmissionReportMassFlow.unit`), umgerechnet
 * aus `reportUnit` (D-12/D-13).
 */
export interface EmissionReportMassFlowEntry {
  date: string | null;
  dateRaw: string | null;
  operatingMode: 'VB' | 'DB' | null;
  value: string | null;
  belowLimit: boolean;
  empty: EmissionReportCellValue['empty'];
  reportUnit: string;
  sourceTable: string | null;
}

/**
 * Die Massenstroeme EINER Komponente (Phase 40, Plan 40-04, D-17): Einzelwerte plus die vom
 * Server gebildeten Mittel je Betriebszustand (`averageCompound`/`averageDirect`) --
 * ausschliesslich der Server rechnet diese Mittel (T-40-11), das Frontend schickt sie nie
 * veraendert zurueck.
 */
export interface EmissionReportMassFlow {
  unit: 'g/h' | 'mg/h' | 'µg TE/h';
  entries: EmissionReportMassFlowEntry[];
  averageCompound: string | null;
  averageDirect: string | null;
}

export interface EmissionReportData {
  schemaVersion: number;
  readable: boolean;
  measurementRange: { start: string | null; end: string | null };
  measurements: EmissionReportMeasurement[];
  /**
   * Seit `schemaVersion` 2 (Plan 40-04, D-17): Massenstroeme der 22 Importkomponenten, nur
   * Komponenten mit mindestens einem Einzelwert-Eintrag (D-16). Aeltere Dokumente
   * (`schemaVersion: 1`) tragen den Schluessel gar nicht -- `[]` steht hier fuer denselben
   * leeren Zustand wie ein fehlender Schluessel, `Object.entries(data.massFlows ?? {})` traegt
   * beide Faelle ohne Sonderfall.
   */
  massFlows?: Record<string, EmissionReportMassFlow> | [];
  log: EmissionReportLogEntry[];
}

// `existingMeasurementCount` wird erst in Plan 35-07 vom Backend geliefert (nicht gemappte,
// serverseitig berechnete Eigenschaft) -- hier schon getypt, damit die nachfolgenden Plaene 09-12
// nicht erneut an den Typen anfassen muessen.
/**
 * Die drei Zuordnungen eines Importvorgangs kommen NICHT als IRI-Zeichenkette, sondern als
 * eingebettetes Objekt -- die Zielentitaeten tragen ihre Anzeigefelder in derselben Lesegruppe
 * (`emissionimport:read`), und API Platform bettet dann ein statt zu verweisen. Das ist die
 * nuetzlichere Form: der heutige Name (D-13) und die Periode stehen damit in jeder Zeile, ohne
 * dass die Seite die Stammdaten noch einmal holen muss.
 *
 * Fehlt die Zuordnung, laesst der Serializer das Feld GANZ weg -- der Wert ist dann `undefined`,
 * nicht `null`. Deshalb sind die Felder optional, und jede Pruefung geht ueber `== null`, das
 * beide Faelle traegt. Ein `=== null` liefe an `undefined` vorbei.
 */
export interface EmissionReportPlantRef {
  '@id': string;
  id: number;
  name: string;
}

export interface EmissionReportKilnRef {
  '@id': string;
  id: number;
  name: string;
}

export interface EmissionReportReportRef {
  '@id': string;
  id: number;
  reportingPeriod: string | null;
}

/**
 * Was beim Speichern der Zuordnung HINAUS geht: dort erwartet API Platform IRI-Zeichenketten.
 * Lesen und Schreiben haben hier also bewusst verschiedene Formen.
 */
export interface EmissionReportImportWrite {
  plant: string | null;
  plantKiln: string | null;
  environmentalData: string | null;
  resultData: EmissionReportData | null;
}

export interface EmissionReportImport {
  id: number;
  originalFilename: string;
  fileSize: number;
  sha256: string;
  uploadedAt: string;
  uploadedByName: string | null;
  status: EmissionReportImportStatus;
  acceptedAt: string | null;
  // T-35-31 (Repudiation, Rule-2-Deviation Plan 35-11): analog zu `uploadedByName`.
  acceptedByName: string | null;
  plant?: EmissionReportPlantRef | null;
  plantKiln?: EmissionReportKilnRef | null;
  environmentalData?: EmissionReportReportRef | null;
  plantNameAtImport: string | null;
  kilnNameAtImport: string | null;
  reportingPeriodAtImport: string | null;
  measurementRangeStart: string | null;
  measurementRangeEnd: string | null;
  existingMeasurementCount: number | null;
  /**
   * Phase 40 (Plan 40-05, D-18): Komponenten aus `resultData.massFlows`, die dieser Import
   * mitbringt UND am Rechenblatt des zugeordneten Ofens bereits einen Oe-Wert tragen --
   * Grundlage des Kollisionshinweises "ersetzt einen bestehenden Wert" (PreviewNotes). Nur in
   * der Einzelansicht gefuellt, in der Sammlung immer `null` (kein N+1). Fehlt die Zuordnung
   * oder existiert kein Rechenblatt: `null` bzw. `[]`.
   */
  existingMassFlowComponents: string[] | null;
  sourceData: EmissionReportData | null;
  resultData: EmissionReportData | null;
}

// Form data types (for create/update)
export interface CompanyFormData {
  name: string;
  csiMember: boolean;
}

export interface PlantFormData {
  name: string;
  company: string; // IRI format: /api/companies/{id}
  region: Region;
  activeUntil?: string | null;
}

export interface PlantKilnFormData {
  name: string;
  plant: string; // IRI format: /api/plants/{id}
  sortOrder: number;
  activeFrom?: string | null;
  activeUntil?: string | null;
  allowsOwnMeasurements: boolean;
}

export interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roles: Role[];
  isActive: boolean;
  company?: string | null; // IRI format: /api/companies/{id}
  plants?: string[]; // IRI format: /api/plants/{id}
  enabledReportCategories: string[];
  plainPassword?: string;
}
